import * as turfHelpers from '@turf/helpers';
import turfTransformRotate from '@turf/transform-rotate';
import turfTransformScale from '@turf/transform-scale';
import { IPoint } from 'libs/utils/map/mapTypes';
import { getMarkerScale, normalizeLat, normalizeLng } from 'libs/utils/world/worldUtils';
import polylabel from 'polylabel';
import { ICoordinates, IImageWorldMarker } from 'react-give-me-map';
import { IXY } from 'react-give-me-map/dist/utils/map/mapTypes';
import { MapRef } from 'react-map-gl';

const loadImage = async (src: string) => {
    return new Promise<HTMLImageElement>((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = (): void => {
            return resolve(img);
        };
    });
};

const computeImageBoundaries = async (marker: IImageWorldMarker, mapRef?: MapRef) => {
    const coordinatesToXY = (c: ICoordinates) => mapRef.project([c.lng, c.lat]);

    if (!marker.elementData.renderAsLayer) {
        return null;
    }

    const zoom = mapRef?.getZoom();

    if (!mapRef) {
        throw new Error('mapRef not found');
    }

    const scale = getMarkerScale(marker, zoom);

    const image = await loadImage(marker.elementData.src);
    const details = {
        scale,
        // 250 is fixed width of every image when loaded
        width: 250 * scale,
        height: 250 * (image.height / image.width) * scale
    };

    const center = coordinatesToXY({ lat: marker.lat, lng: marker.lng });

    return rotateImage({
        mapRef,
        center,
        rotation: marker.rotate,
        width: details.width,
        height: details.height
    });
};

export const rotateImage = (props: {
    mapRef: MapRef;
    center: IXY;
    rotation: number;
    width: number;
    height: number;
}) => {
    const { mapRef, center, rotation, width, height } = props;
    const XYToCoordinates = (p: IPoint) => {
        const latLng = mapRef?.unproject([p.x, p.y]);
        return {
            lat: normalizeLat(latLng.lat),
            lng: normalizeLng(latLng.lng)
        };
    };

    const rotationRadians = (-rotation ?? 0) * (Math.PI / 180);

    const getRotatedX = (p: IPoint) =>
        center.x +
        (p.x - center.x) * Math.cos(rotationRadians) +
        (p.y - center.y) * Math.sin(rotationRadians);
    const getRotatedY = (p: IPoint) =>
        center.y -
        (p.x - center.x) * Math.sin(rotationRadians) +
        (p.y - center.y) * Math.cos(rotationRadians);

    const getRotated = (p: IPoint) => {
        return {
            x: getRotatedX(p),
            y: getRotatedY(p)
        };
    };

    const baseX = center.x - width / 2;
    const baseY = center.y - height / 2;

    const XYLeftTop = {
        x: baseX,
        y: baseY
    };

    const XYLeftBottom = {
        x: baseX,
        y: baseY + height
    };

    const XYRightTop = {
        x: baseX + width,
        y: baseY
    };

    const XYRightBottom = {
        x: baseX + width,
        y: baseY + height
    };

    return {
        bottomLeft: XYToCoordinates(getRotated(XYLeftBottom)),
        topLeft: XYToCoordinates(getRotated(XYLeftTop)),
        bottomRight: XYToCoordinates(getRotated(XYRightBottom)),
        topRight: XYToCoordinates(getRotated(XYRightTop))
    };
};

export const useCoordinatesComputations = (mapRef: React.RefObject<MapRef>) => {
    return {
        computeImageBoundaries: (marker: IImageWorldMarker) =>
            computeImageBoundaries(marker, mapRef?.current)
    };
};

export const rotateRectangleCoordinates = (
    mapRef: MapRef,
    coordinates: ICoordinates[],
    rotation: number
) => {
    const positions = coordinates.map(({ lat, lng }) => {
        const xy = mapRef?.project([lng, lat]);
        return { x: xy.x, y: xy.y };
    });

    const result = rotateImage({
        mapRef: mapRef,
        rotation,
        center: {
            x: (positions[0].x + positions[1].x) / 2,
            y: (positions[0].y + positions[3].y) / 2
        },
        width: positions[1].x - positions[0].x,
        height: positions[3].y - positions[0].y
    });

    return [result.topLeft, result.topRight, result.bottomRight, result.bottomLeft];
};

export const rotateAndScalePolygon = (
    originalCoordinates: ICoordinates[],
    rotation: number,
    scale: number
) => {
    console.log('originalCoordinates', originalCoordinates);
    const hasOnlyTwoCoordinates = originalCoordinates.length === 2;

    const coordinates = hasOnlyTwoCoordinates
        ? [
              originalCoordinates[0],
              originalCoordinates[1],
              originalCoordinates[1],
              originalCoordinates[0]
          ]
        : [...originalCoordinates, originalCoordinates[0]];

    const polygon = turfHelpers.polygon([coordinates.map((e) => [e.lng, e.lat])]);
    const [centerLng, centerLat] = polylabel([coordinates.map(({ lng, lat }) => [lng, lat])]);

    const scaledPolygon = turfTransformScale(polygon, scale);
    const rotatedAndScaledPolygon = turfTransformRotate(scaledPolygon, rotation, {
        pivot: [centerLng, centerLat]
    });
    const newCoordinates = rotatedAndScaledPolygon.geometry.coordinates[0]
        .map((e) => ({
            lat: e[1],
            lng: e[0]
        }))
        .slice(0, hasOnlyTwoCoordinates ? -2 : -1);

    return newCoordinates;
};

export const recalculateCoordinatesForNewImage = (
    mapRef: MapRef,
    nonRotatedCoordinates: ICoordinates[],
    newImage: { width: number; height: number }
) => {
    const points = nonRotatedCoordinates.map((e) => mapRef.project([e.lng, e.lat]));
    const dimensions = {
        width: points[1].x - points[0].x,
        height: points[2].y - points[1].y
    };
    const dimensionsRatio = dimensions.height / dimensions.width;
    const newDimensionsRatio = newImage.height / newImage.width;

    const scaleFactor = newDimensionsRatio / dimensionsRatio;

    const currentHeightCenter = (points[2].y + points[1].y) / 2;

    const newTop = currentHeightCenter - scaleFactor * (dimensions.height / 2);
    const newBottom = currentHeightCenter + scaleFactor * (dimensions.height / 2);

    const newPoints: IXY[] = [
        { x: points[0].x, y: newTop },
        { x: points[1].x, y: newTop },
        { x: points[2].x, y: newBottom },
        { x: points[3].x, y: newBottom }
    ];

    const newCoordinates = newPoints.map((e) => mapRef.unproject([e.x, e.y]));

    return newCoordinates;
};

export const recalculateCoordinatesForNewText = (
    mapRef: MapRef,
    nonRotatedCoordinates: ICoordinates[],
    newImage: { width: number; height: number },
    increaseDirection: 'width' | 'height'
) => {
    const points = nonRotatedCoordinates.map((e) => mapRef.project([e.lng, e.lat]));
    const dimensions = {
        width: points[1].x - points[0].x,
        height: points[2].y - points[1].y
    };
    if (increaseDirection === 'height') {
        const dimensionsRatio = dimensions.height / dimensions.width;
        const newDimensionsRatio = newImage.height / newImage.width;

        const scaleFactor = newDimensionsRatio / dimensionsRatio;

        const currentHeightCenter = (points[2].y + points[1].y) / 2;

        const newTop = currentHeightCenter - scaleFactor * (dimensions.height / 2);
        const newBottom = currentHeightCenter + scaleFactor * (dimensions.height / 2);

        const newPoints: IXY[] = [
            { x: points[0].x, y: newTop },
            { x: points[1].x, y: newTop },
            { x: points[2].x, y: newBottom },
            { x: points[3].x, y: newBottom }
        ];

        const newCoordinates = newPoints.map((e) => mapRef.unproject([e.x, e.y]));

        return newCoordinates;
    }

    const dimensionsRatio = dimensions.width / dimensions.height;
    const newDimensionsRatio = newImage.width / newImage.height;

    const scaleFactor = newDimensionsRatio / dimensionsRatio;

    const currentWidthCenter = (points[1].x + points[0].x) / 2;

    const newLeft = currentWidthCenter - scaleFactor * (dimensions.width / 2);
    const newRight = currentWidthCenter + scaleFactor * (dimensions.width / 2);

    const newPoints: IXY[] = [
        { x: newLeft, y: points[0].y },
        { x: newRight, y: points[1].y },
        { x: newRight, y: points[2].y },
        { x: newLeft, y: points[3].y }
    ];

    const newCoordinates = newPoints.map((e) => mapRef.unproject([e.x, e.y]));

    return newCoordinates;
};

export const recalculateCoordinatesForNewWidth = (
    mapRef: MapRef,
    nonRotatedCoordinates: ICoordinates[],
    newWidth: number
) => {
    const points = nonRotatedCoordinates.map((e) => mapRef.project([e.lng, e.lat]));
    const dimensions = {
        width: points[1].x - points[0].x,
        height: points[2].y - points[1].y
    };
    const scaleFactor = newWidth / dimensions.width;

    const currentWidthCenter = (points[0].x + points[1].x) / 2;
    const currentHeightCenter = (points[2].y + points[1].y) / 2;

    const newTop = currentHeightCenter - scaleFactor * (dimensions.height / 2);
    const newBottom = currentHeightCenter + scaleFactor * (dimensions.height / 2);
    const newLeft = currentWidthCenter - scaleFactor * (dimensions.width / 2);
    const newRight = currentWidthCenter + scaleFactor * (dimensions.width / 2);

    const newPoints: IXY[] = [
        { x: newLeft, y: newTop },
        { x: newRight, y: newTop },
        { x: newLeft, y: newBottom },
        { x: newRight, y: newBottom }
    ];

    const newCoordinates = newPoints.map((e) => mapRef.unproject([e.x, e.y]));

    return newCoordinates;
};
